import React, { useState, useEffect } from 'react';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Clock, TrendingUp, Zap, MapPin, Calendar, BarChart3 } from 'lucide-react';
import api from '../lib/api';
import { PRACTICE_STATUS } from '../lib/constants';
import { differenceInDays, parseISO, format } from 'date-fns';
import { it } from 'date-fns/locale';

export default function AnalyticsPage() {
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPractices();
  }, []);

  const loadPractices = async () => {
    try {
      const response = await api.get('/api/practices');
      setPractices(response.data.practices || []);
    } catch (error) {
      console.error('Failed to load practices:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate analytics
  const analytics = React.useMemo(() => {
    if (practices.length === 0) return null;

    const completed = practices.filter(p => p.status === PRACTICE_STATUS.COMPLETATA_IN_COMITATO);
    const inProgress = practices.filter(p => 
      [PRACTICE_STATUS.IN_LAVORAZIONE, PRACTICE_STATUS.DA_INTEGRARE].includes(p.status)
    );

    // Tempo medio di lavorazione (pratiche completate)
    const completionTimes = completed
      .filter(p => p.completed_at && p.created_at)
      .map(p => differenceInDays(parseISO(p.completed_at), parseISO(p.created_at)));
    
    const avgCompletionTime = completionTimes.length > 0
      ? (completionTimes.reduce((a, b) => a + b, 0) / completionTimes.length).toFixed(1)
      : 0;

    const minCompletionTime = completionTimes.length > 0 ? Math.min(...completionTimes) : 0;
    const maxCompletionTime = completionTimes.length > 0 ? Math.max(...completionTimes) : 0;

    // Potenza totale e media
    const totalPower = completed.reduce((sum, p) => sum + (p.input_data?.potenza_stimata || 0), 0);
    const avgPower = completed.length > 0 ? totalPower / completed.length : 0;

    // Distribuzione per città (top 5)
    const cityCounts = {};
    practices.forEach(p => {
      const city = p.input_data?.citta || 'Non specificato';
      cityCounts[city] = (cityCounts[city] || 0) + 1;
    });
    const topCities = Object.entries(cityCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    // Distribuzione per tipologia
    const typeCounts = {
      tetto: practices.filter(p => p.input_data?.tipologia === 'tetto').length,
      terra: practices.filter(p => p.input_data?.tipologia === 'terra').length,
    };

    // Pratiche per mese (ultimi 6 mesi)
    const monthlyData = {};
    practices.forEach(p => {
      const month = format(parseISO(p.created_at), 'MMM yyyy', { locale: it });
      monthlyData[month] = (monthlyData[month] || 0) + 1;
    });
    const monthlyStats = Object.entries(monthlyData).slice(-6);

    // Tasso di successo
    const successRate = practices.length > 0
      ? ((completed.length / practices.length) * 100).toFixed(1)
      : 0;

    const rejectionRate = practices.length > 0
      ? ((practices.filter(p => p.status === PRACTICE_STATUS.RIFIUTATA).length / practices.length) * 100).toFixed(1)
      : 0;

    return {
      avgCompletionTime,
      minCompletionTime,
      maxCompletionTime,
      totalPower: totalPower.toFixed(2),
      avgPower: avgPower.toFixed(2),
      topCities,
      typeCounts,
      monthlyStats,
      successRate,
      rejectionRate,
      completed: completed.length,
      inProgress: inProgress.length,
      total: practices.length,
    };
  }, [practices]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Caricamento analytics...</p>
        </div>
      </AppLayout>
    );
  }

  if (!analytics) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Nessun dato disponibile</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Analytics & KPI</h1>
          <p className="text-muted-foreground">Statistiche e metriche delle pratiche</p>
        </div>

        {/* Tempi di Lavorazione */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Tempi di Lavorazione</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="card-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Tempo Medio
                  </CardTitle>
                  <Clock className="h-4 w-4 text-blue-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold tabular-nums">
                  {analytics.avgCompletionTime}
                </div>
                <p className="text-xs text-muted-foreground mt-1">giorni per pratica</p>
              </CardContent>
            </Card>

            <Card className="card-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Tempo Minimo
                  </CardTitle>
                  <TrendingUp className="h-4 w-4 text-emerald-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold tabular-nums">
                  {analytics.minCompletionTime}
                </div>
                <p className="text-xs text-muted-foreground mt-1">giorni (record)</p>
              </CardContent>
            </Card>

            <Card className="card-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Tempo Massimo
                  </CardTitle>
                  <Clock className="h-4 w-4 text-amber-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold tabular-nums">
                  {analytics.maxCompletionTime}
                </div>
                <p className="text-xs text-muted-foreground mt-1">giorni</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Potenza */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Potenza Installata</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="card-shadow bg-primary text-primary-foreground">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium opacity-90">
                    Potenza Totale
                  </CardTitle>
                  <Zap className="h-5 w-5 opacity-90" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-semibold tabular-nums">
                  {analytics.totalPower} kW
                </div>
                <p className="text-xs opacity-75 mt-1">
                  {analytics.completed} impianti completati
                </p>
              </CardContent>
            </Card>

            <Card className="card-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Potenza Media
                  </CardTitle>
                  <BarChart3 className="h-4 w-4 text-blue-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-semibold tabular-nums">
                  {analytics.avgPower} kW
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  per impianto
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Performance */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Performance</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="card-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Tasso di Successo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold tabular-nums text-emerald-600">
                  {analytics.successRate}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.completed} completate su {analytics.total}
                </p>
              </CardContent>
            </Card>

            <Card className="card-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Tasso di Rifiuto
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold tabular-nums text-rose-600">
                  {analytics.rejectionRate}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  pratiche rifiutate
                </p>
              </CardContent>
            </Card>

            <Card className="card-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  In Lavorazione
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold tabular-nums text-blue-600">
                  {analytics.inProgress}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  pratiche attive
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Distribuzione Geografica */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Top 5 Città
              </CardTitle>
              <CardDescription>Pratiche per località</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics.topCities.map(([city, count], index) => (
                  <div key={city} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </div>
                      <span className="font-medium">{city}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-24 bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{
                            width: `${(count / analytics.total) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="text-sm font-semibold tabular-nums w-8 text-right">
                        {count}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader>
              <CardTitle>Distribuzione per Tipologia</CardTitle>
              <CardDescription>Tetto vs Terra</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">A Tetto</span>
                    <span className="text-sm font-semibold tabular-nums">
                      {analytics.typeCounts.tetto} pratiche
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div
                      className="bg-blue-500 h-3 rounded-full"
                      style={{
                        width: `${(analytics.typeCounts.tetto / analytics.total) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {((analytics.typeCounts.tetto / analytics.total) * 100).toFixed(1)}%
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">A Terra</span>
                    <span className="text-sm font-semibold tabular-nums">
                      {analytics.typeCounts.terra} pratiche
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div
                      className="bg-emerald-500 h-3 rounded-full"
                      style={{
                        width: `${(analytics.typeCounts.terra / analytics.total) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {((analytics.typeCounts.terra / analytics.total) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trend Mensile */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Trend Pratiche (Ultimi Mesi)
            </CardTitle>
            <CardDescription>Numero di pratiche create per mese</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.monthlyStats.map(([month, count]) => (
                <div key={month} className="flex items-center gap-4">
                  <span className="w-24 text-sm font-medium">{month}</span>
                  <div className="flex-1 bg-muted rounded-full h-8 relative">
                    <div
                      className="bg-gradient-to-r from-primary to-blue-400 h-8 rounded-full flex items-center justify-end pr-3"
                      style={{
                        width: `${(count / Math.max(...analytics.monthlyStats.map(([, c]) => c))) * 100}%`,
                        minWidth: '40px',
                      }}
                    >
                      <span className="text-xs font-semibold text-white">{count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
